import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { AuthProvider } from './contexts/AuthContext';
import { PremiumProvider } from './contexts/PremiumContext';
import { AccessControlProvider } from './contexts/AccessControlContext';
import { CategoryGameProvider, useCategoryGameState } from './contexts/GameContext';
import { GameStartProvider, useGameStart } from './contexts/GameStartContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AsyncErrorBoundary } from './components/AsyncErrorBoundary';
import { queryClient } from './config/queryClient';
import { Layout } from './components/layout';
import { CategoryGamePage } from './pages/CategoryGamePage';
import { AuthCallback } from './pages/AuthCallback';
import { JoinLeaderboardPage } from './pages/JoinLeaderboardPage';
import { PremiumSuccessPage } from './pages/PremiumSuccessPage';
import { PremiumCancelPage } from './pages/PremiumCancelPage';
import { SettingsPage } from './pages/SettingsPage';
import { AccountManagementPage } from './pages/settings/AccountManagementPage';
import { SubscriptionManagementPage } from './pages/settings/SubscriptionManagementPage';
import { PaymentMethodsPage } from './pages/settings/PaymentMethodsPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import SupportPage from './pages/SupportPage';
import FAQPage from './pages/FAQPage';
import HowToPlayPage from './pages/HowToPlayPage';
import BaronWuffettPage from './pages/BaronWuffettPage';
import WordleForStocksPage from './pages/WordleForStocksPage';
import BlogIndexPage from './pages/blog/BlogIndexPage';
import HowToInvestPage from './pages/blog/HowToInvestPage';
import LearnToInvestPage from './pages/blog/LearnToInvestPage';
import PaperTradingPage from './pages/blog/PaperTradingPage';
import StockPickingPage from './pages/blog/StockPickingPage';
import CompareThePairBlogPage from './pages/blog/CompareThePairBlogPage';
import TriviaRushBlogPage from './pages/blog/TriviaRushBlogPage';
import WhatIsPeRatioPage from './pages/blog/WhatIsPeRatioPage';
import WhatIsMarketCapPage from './pages/blog/WhatIsMarketCapPage';
import WhatIsEpsPage from './pages/blog/WhatIsEpsPage';
import WhatIsDividendYieldPage from './pages/blog/WhatIsDividendYieldPage';
import WhatIsRoePage from './pages/blog/WhatIsRoePage';
import GrossVsOperatingMarginPage from './pages/blog/GrossVsOperatingMarginPage';
import RevenueVsEarningsPage from './pages/blog/RevenueVsEarningsPage';
import WhatIsFreeCashFlowPage from './pages/blog/WhatIsFreeCashFlowPage';
import DebtToEquityRatioPage from './pages/blog/DebtToEquityRatioPage';
import HowToReadABalanceSheetPage from './pages/blog/HowToReadABalanceSheetPage';
import StockWordlePage from './pages/blog/StockWordlePage';
import FinanceWordlePage from './pages/blog/FinanceWordlePage';
import DailyStockChallengePage from './pages/blog/DailyStockChallengePage';
import FreeStockMarketGamesPage from './pages/blog/FreeStockMarketGamesPage';
import WsbStocksPage from './pages/blog/WsbStocksPage';
import HowToStartInvestingWith100DollarsPage from './pages/blog/HowToStartInvestingWith100DollarsPage';
import InvestingMistakesBeginnersMakePage from './pages/blog/InvestingMistakesBeginnersMakePage';
import IndexFundsVsStocksPage from './pages/blog/IndexFundsVsStocksPage';
import HowToReadAStockChartPage from './pages/blog/HowToReadAStockChartPage';
import HowToValueAStockPage from './pages/blog/HowToValueAStockPage';
import StartPage from './pages/StartPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { YesterdayPage } from './pages/YesterdayPage';
import { MembershipPage } from './pages/MembershipPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { ProgressPage } from './pages/ProgressPage';
import { CoursePage } from './pages/CoursePage';
import { TriviaRushPage } from './pages/TriviaRushPage';
import { CompareThePairPage } from './pages/CompareThePairPage';
import { InvestmentPhilosophyPage } from './pages/InvestmentPhilosophyPage';
import { LearnStocksDailyPage } from './pages/LearnStocksDailyPage';
import { TriviaRushDemoPage } from './pages/TriviaRushDemoPage';
import { RushLeaderboardPage } from './pages/RushLeaderboardPage';
import BattleRushPage from './pages/BattleRushPage';
import HomePage from './pages/HomePage';
import { CategoryArchivePage } from './pages/CategoryArchivePage';
import { AnswersPage } from './pages/AnswersPage';
import { DevTestAnyPage } from './pages/DevTestAnyPage';
import { EmailUnsubscribePage } from './pages/EmailUnsubscribePage';
import { ToastContainer, useToast } from './components/ui/Toast';
import { performStorageCleanup } from './utils/localStorageCleanup';
import { migrateDeviceIdIfNeeded } from './utils/deviceId';
import { initializeRevenueCat } from './services/revenuecat';
import { initializeNotifications } from './services/notifications';
import { initializePushNotifications } from './services/pushNotifications';
import { Analytics } from '@vercel/analytics/react';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { AnalyticsPageViews } from './components/AnalyticsPageViews';
import './index.css';
import { StockCategoryFilter } from './types/stockCategories';
import { ALL_CATEGORIES, DEFAULT_CATEGORY } from './config/categoryConfig';
import { NativeAuthGate } from './components/auth/NativeAuthGate';
import { useDeepLinkAuth } from './hooks/useDeepLinkAuth';
import { useAppLifecycle } from './hooks/useAppLifecycle';
import { isNativeApp } from './utils/platform';


// Priority categories for routing (imported from central config)
const PRIORITY_CATEGORIES = ALL_CATEGORIES as readonly string[];

// Valid stock categories for routing (imported from central config)
const VALID_CATEGORIES: StockCategoryFilter[] = [...ALL_CATEGORIES];

// Routes that should hide the streak counter and timer from the header
const HIDE_STREAK_TIMER_ROUTES = new Set<string>([
  '/membership',
  '/portfolio',
  '/course',
  '/learn-stocks-daily'
]);

// Listens for OAuth deep link callbacks on native (must be inside Router, outside NativeAuthGate)
const DeepLinkAuthListener: React.FC = () => {
  useDeepLinkAuth();
  return null;
};

// Reloads the app when returning from background after game date rollover (native only)
const AppLifecycleListener: React.FC = () => {
  useAppLifecycle();
  return null;
};

// Router debug component to log location changes (development only)
const RouterDebugger: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Only log in development mode and only when location actually changes
    if (process.env.NODE_ENV === 'development') {
      console.log('🧭 [Router] Location changed:', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
        key: location.key
      });
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('wallstreetle-route-changed', {
          detail: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash
          }
        })
      );
    }
  }, [location]);

  return null;
};

// Dynamic category route component that uses location for proper key
const DynamicCategoryRoute: React.FC = () => {
  const location = useLocation();

  // Use location.pathname for key to ensure remounting on route changes
  return <CategoryGamePage key={`dynamic-category-${location.pathname}`} />;
};

// Layout wrapper to extract category from route
const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const gameState = useCategoryGameState();
  const { hasGameStarted } = useGameStart();

  // Normalize path to handle optional trailing slashes
  const normalizedPath = location.pathname.endsWith('/') && location.pathname !== '/'
    ? location.pathname.slice(0, -1)
    : location.pathname;

  // Extract category from pathname (e.g., "/tech" -> "tech", "/popular" -> "popular")
  const pathSegment = normalizedPath.slice(1).split('/')[0];

  // Only use the path segment if it's a valid category, otherwise default to configured default
  // This prevents errors when navigating to non-category routes like /premium/success, /auth/callback, etc.
  const currentCategory = (VALID_CATEGORIES.includes(pathSegment as StockCategoryFilter)
    ? pathSegment
    : DEFAULT_CATEGORY) as StockCategoryFilter;

  // Extract gameDate from gameState
  const gameDate = gameState.gameState.gameDate;

  const isHomePage = normalizedPath === '/';
  const isArchivePage = normalizedPath.endsWith('/archive');
  const isGamePage = !isHomePage && !isArchivePage && (VALID_CATEGORIES.includes(pathSegment as StockCategoryFilter));

  return (
    <Layout
      currentCategory={currentCategory}
      gameDate={gameDate}
      isGameComplete={gameState.gameState.isGameComplete}
      hideSubHeader={!isGamePage}
      hideBackBar={isHomePage || isGamePage}
      hideFooter={isHomePage}
      hideStreakAndTimer={isHomePage || isArchivePage || HIDE_STREAK_TIMER_ROUTES.has(normalizedPath) || normalizedPath === '/progress'}
      mobileTopBanner={undefined}
      hasGameStarted={hasGameStarted}
      showSubFooter={isGamePage}
      hideToolbelt={!isGamePage || isArchivePage}
      minimal={isHomePage}
    >
      {children}
    </Layout>
  );
};

const AppContent: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <>
      <Router>
        <RouterDebugger />
        <AnalyticsPageViews />
        <DeepLinkAuthListener />
        <AppLifecycleListener />
        <NativeAuthGate>
        <Routes>
          {/* Start page - category selection landing (no Layout wrapper) */}
          <Route
            path="/start"
            element={<StartPage />}
          />

          {/* Trivia Rush - standalone arcade minigame (no Layout wrapper) */}
          {process.env.NODE_ENV === 'development' && (
            <Route
              path="/trivia-rush/demo"
              element={<TriviaRushDemoPage />}
            />
          )}
          <Route
            path="/trivia-rush/battle/:code"
            element={<BattleRushPage />}
          />
          <Route
            path="/trivia-rush/leaderboard"
            element={<RushLeaderboardPage />}
          />
          <Route
            path="/trivia-rush"
            element={<TriviaRushPage />}
          />
          {/* Compare the Pair - standalone daily comparison game (no Layout wrapper) */}
          <Route
            path="/compare-the-pair"
            element={<CompareThePairPage />}
          />
          {/* Investment Philosophy Quiz - standalone client-side quiz (no Layout wrapper) */}
          <Route
            path="/investment-philosophy"
            element={<InvestmentPhilosophyPage />}
          />

          {/* All other routes wrapped in Layout */}
          <Route path="/*" element={
            <LayoutWrapper>
              <Routes>
                {/* Root page - landing/home page */}
                <Route
                  path="/"
                  element={<HomePage />}
                />

                {/* Category-specific routes */}
            {PRIORITY_CATEGORIES.map((category) => (
              <Route
                key={category}
                path={`/${category}`}
                element={
                  <CategoryGamePage
                    key={`category-${category}`}
                    routeCategory={category as StockCategoryFilter}
                  />
                }
              />
            ))}

            {/* OAuth callback route */}
            <Route
              path="/auth/callback"
              element={<AuthCallback />}
            />

            {/* Join leaderboard via invite link */}
            <Route
              path="/join-leaderboard"
              element={<JoinLeaderboardPage />}
            />

            {/* Premium success page */}
            <Route
              path="/premium/success"
              element={<PremiumSuccessPage />}
            />

            {/* Premium cancel page */}
            <Route
              path="/premium/cancel"
              element={<PremiumCancelPage />}
            />

            {/* Membership page */}
            <Route
              path="/membership"
              element={<MembershipPage />}
            />

            {/* Portfolio page */}
            <Route
              path="/portfolio"
              element={<PortfolioPage />}
            />

            {/* Settings hub */}
            <Route
              path="/settings"
              element={<SettingsPage />}
            />

            {/* Settings sub-routes */}
            <Route
              path="/settings/account"
              element={<AccountManagementPage />}
            />
            <Route
              path="/settings/subscription"
              element={<SubscriptionManagementPage />}
            />
            <Route
              path="/settings/payment-methods"
              element={<PaymentMethodsPage />}
            />

            {/* Feedback page */}
            <Route
              path="/feedback"
              element={<FeedbackPage />}
            />

            {/* Email unsubscribe page */}
            <Route
              path="/email/unsubscribe/:token"
              element={<EmailUnsubscribePage />}
            />

{/* Password Reset page */}
            <Route
              path="/reset-password"
              element={<PasswordResetPage />}
            />

            {/* Terms & Conditions page */}
            <Route
              path="/policies/terms"
              element={<TermsPage />}
            />

            {/* Privacy Policy page */}
            <Route
              path="/policies/privacy"
              element={<PrivacyPage />}
            />

            {/* Support page */}
            <Route
              path="/support"
              element={<SupportPage />}
            />

            {/* FAQ page */}
            <Route
              path="/faq"
              element={<FAQPage />}
            />

            {/* How to Play page */}
            <Route
              path="/how-to-play"
              element={<HowToPlayPage />}
            />

            {/* Baron Wuffett SEO page */}
            <Route
              path="/baron-wuffett"
              element={<BaronWuffettPage />}
            />

            {/* Wordle for Stocks mission page */}
            <Route
              path="/wordle-for-stocks"
              element={<WordleForStocksPage />}
            />

            {/* Blog pages */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/how-to-invest" element={<HowToInvestPage />} />
            <Route path="/blog/learn-to-invest" element={<LearnToInvestPage />} />
            <Route path="/blog/paper-trading" element={<PaperTradingPage />} />
            <Route path="/blog/stock-picking" element={<StockPickingPage />} />
            <Route path="/blog/compare-the-pair" element={<CompareThePairBlogPage />} />
            <Route path="/blog/trivia-rush" element={<TriviaRushBlogPage />} />
            {/* Concept articles */}
            <Route path="/blog/what-is-pe-ratio" element={<WhatIsPeRatioPage />} />
            <Route path="/blog/what-is-market-cap" element={<WhatIsMarketCapPage />} />
            <Route path="/blog/what-is-eps" element={<WhatIsEpsPage />} />
            <Route path="/blog/what-is-dividend-yield" element={<WhatIsDividendYieldPage />} />
            <Route path="/blog/what-is-roe" element={<WhatIsRoePage />} />
            <Route path="/blog/gross-vs-operating-margin" element={<GrossVsOperatingMarginPage />} />
            <Route path="/blog/revenue-vs-earnings" element={<RevenueVsEarningsPage />} />
            <Route path="/blog/what-is-free-cash-flow" element={<WhatIsFreeCashFlowPage />} />
            <Route path="/blog/debt-to-equity-ratio" element={<DebtToEquityRatioPage />} />
            <Route path="/blog/how-to-read-a-balance-sheet" element={<HowToReadABalanceSheetPage />} />
            {/* Game / product articles */}
            <Route path="/blog/stock-wordle" element={<StockWordlePage />} />
            <Route path="/blog/finance-wordle" element={<FinanceWordlePage />} />
            <Route path="/blog/daily-stock-challenge" element={<DailyStockChallengePage />} />
            <Route path="/blog/free-stock-market-games" element={<FreeStockMarketGamesPage />} />
            <Route path="/blog/wsb-stocks" element={<WsbStocksPage />} />
            {/* Beginner guides */}
            <Route path="/blog/how-to-start-investing-with-100-dollars" element={<HowToStartInvestingWith100DollarsPage />} />
            <Route path="/blog/investing-mistakes-beginners-make" element={<InvestingMistakesBeginnersMakePage />} />
            <Route path="/blog/index-funds-vs-stocks" element={<IndexFundsVsStocksPage />} />
            <Route path="/blog/how-to-read-a-stock-chart" element={<HowToReadAStockChartPage />} />
            <Route path="/blog/how-to-value-a-stock" element={<HowToValueAStockPage />} />

            {/* Debug route for testing hooks and components */}
            {process.env.NODE_ENV === 'development' && (
              <Route
                path="/debug"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    {React.createElement(React.lazy(() => import('./pages/DebugPage')))}
                  </React.Suspense>
                }
              />
            )}

            {/* Dev-only: Test any historical game without saving data */}
            {process.env.NODE_ENV === 'development' && (
              <Route
                path="/dev-test-any"
                element={<DevTestAnyPage />}
              />
            )}

            {/* Dev-only: Retention analytics dashboard */}
            {process.env.NODE_ENV === 'development' && (
              <Route
                path="/dev/analytics"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    {React.createElement(React.lazy(() => import('./pages/DevAnalyticsPage')))}
                  </React.Suspense>
                }
              />
            )}

            {/* Dev-only: Chart preview for special data */}
            {process.env.NODE_ENV === 'development' && (
              <Route
                path="/dev/chart-preview"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    {React.createElement(React.lazy(() => import('./pages/DevChartPreviewPage')))}
                  </React.Suspense>
                }
              />
            )}

            {/* Course - fundamental analysis education */}
            <Route
              path="/course"
              element={<CoursePage />}
            />

            {/* Progress page - stocks progress tracker */}
            <Route
              path="/progress"
              element={<ProgressPage />}
            />

            {/* Learn Stocks Daily - daily educational stock breakdown */}
            <Route
              path="/learn-stocks-daily"
              element={<LearnStocksDailyPage />}
            />

            {/* Yesterday page for WallStreetBets (secret, not in nav) */}
            <Route
              path="/wallstreetbets/yesterday"
              element={<YesterdayPage />}
            />

            {/* Yesterday's answers, free categories only (SEO); never today's puzzle */}
            <Route
              path="/answers"
              element={<AnswersPage />}
            />

            {/* Category archive pages (must be before /:category catch-all) */}
            {PRIORITY_CATEGORIES.map((cat) => (
              <Route
                key={`${cat}-archive`}
                path={`/${cat}/archive`}
                element={<CategoryArchivePage />}
              />
            ))}

            {/* Future categories route (validates against all valid categories) */}
            <Route
              path="/:category"
              element={<DynamicCategoryRoute />}
            />

                {/* Redirect any unknown routes to default category */}
                <Route path="*" element={<Navigate to={`/${DEFAULT_CATEGORY}`} replace />} />
              </Routes>
            </LayoutWrapper>
          } />
        </Routes>
        </NativeAuthGate>
      </Router>

      {/* Vercel Analytics */}
      <Analytics />

      {/* Analytics cookie consent (web only, shown only when GA4 is configured) */}
      <CookieConsentBanner />

      {/* Global Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

    </>
  );
};

const App: React.FC = () => {
  // Perform localStorage cleanup on app initialization
  // This ensures stale data is cleared daily at game reset time (02:00 UTC)
  // Mobile devices get more aggressive cleanup due to stricter storage limits
  useEffect(() => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    let apiHostForLogs = '(relative)';
    if (apiBaseUrl) {
      try {
        const parsed = new URL(apiBaseUrl);
        apiHostForLogs = `${parsed.protocol}//${parsed.host}`;
      } catch {
        apiHostForLogs = apiBaseUrl;
      }
    }

    // Keep this as warn so it is visible in iOS device logs (info/log are silenced in production).
    console.warn('[StartupConfig]', {
      nodeEnv: process.env.NODE_ENV,
      appEnv: process.env.REACT_APP_ENV || '',
      platform: isNativeApp() ? 'native' : 'web',
      apiBaseUrl: apiHostForLogs,
      hasSupabaseUrl: Boolean(process.env.REACT_APP_SUPABASE_URL),
      hasSupabaseAnonKey: Boolean(process.env.REACT_APP_SUPABASE_ANON_KEY),
    });

    try {
      // Migrate device_id from legacy key to new key
      migrateDeviceIdIfNeeded();

      // Always perform cleanup on app load to ensure stale data is removed
      performStorageCleanup();
    } catch (error) {
      console.warn('Failed to perform startup cleanup:', error);
    }

    // Temporary: opt into paywall debug alerts via `?paywall_debug=1` (or `=0` to disable).
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = params.get('paywall_debug');
      if (flag === '1') window.localStorage.setItem('paywall_debug', '1');
      else if (flag === '0') window.localStorage.removeItem('paywall_debug');
    } catch {}

    // Initialize RevenueCat SDK for Apple IAP (no-ops on web)
    initializeRevenueCat().catch(() => {});

    // Initialize local push notifications (no-ops on web)
    initializeNotifications().catch(() => {});

    // Initialize remote push notifications (no-ops on web; only registers if previously granted)
    initializePushNotifications().catch(() => {});
  }, []); // Run once on mount

  return (
    <ErrorBoundary>
      <AsyncErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <TutorialProvider>
                <AuthProvider>
                  <PremiumProvider>
                    <AccessControlProvider>
                      <ToastProvider>
                        <CategoryGameProvider>
                          <GameStartProvider>
                            <AppContent />
                          </GameStartProvider>
                        </CategoryGameProvider>
                      </ToastProvider>
                    </AccessControlProvider>
                  </PremiumProvider>
                </AuthProvider>
              </TutorialProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </AsyncErrorBoundary>
    </ErrorBoundary>
  );
};

export default App;
